// Le moteur des visites guidées (D-091) : une surimpression posée sur document.body, qui survit aux
// changements de page de l'app (#/…). Pour chaque étape : l'élément visé (data-guide) est amené à
// l'écran et éclairé (un « trou » dans un voile sombre), une bulle l'explique, une pilule dit où l'on
// en est. Deux façons d'avancer :
//   * « Suivant » (visites de la démo, et étapes sans geste) : le voile bloque tout, l'élément compris ;
//   * « faire » (vrais clubs, et « Montre-moi » dans la démo, étape avec `tap`) : seul l'élément éclairé reste
//     touchable (dans la démo, « Suivant » reste là et saute les étapes qui supposent le geste) ; la personne
//     fait le geste, puis le guide attend que l'élément de l'étape suivante apparaisse (afterGesture : un
//     formulaire ouvert se remplit d'abord ; une étape suivante sur une autre page, le guide y mène ; sinon
//     l'élément du geste reste cerclé, la page libre : un assistant en plusieurs écrans, un toucher à côté).
// Un élément absent (rien encore : pas de drill, pas de séance) : l'étape est sautée ; la visite s'arrête
// sur « Rien à montrer » si aucune ne l'avait. La casquette de la visite est prise d'abord (hatFor).
// scripts/e2e-tours.mjs rejoue chaque visite en démo, scripts/e2e-showme.mjs en vrai geste.
// Rien ne bouge avec prefers-reduced-motion ; les zones sûres du téléphone sont respectées (styles.css,
// gd-*). L'état vit dans sessionStorage : une visite reprend après le changement de compte de la démo.
import { TOURS, stepsFor, richText, tourModule, type Tour, type TourStep } from './tours';
import { ARTICLES, shownWith } from '../help/articles';
import { modOn, mvOpen, siteOn, switchHat, workTeams, type Ctx } from '../ui/context';
import { hatFor } from './hat';
import { loadAgenda } from '../ui/agenda';
import { toast } from '../ui/dom';

type Phase = 'idle' | 'seek' | 'step' | 'wait' | 'end';
/** `shown` : étapes affichées ; `skipped` : étapes sautées faute d'élément (la fin le dit). */
interface Run { id: string; i: number; demo: boolean; shown?: number; skipped?: number;
  /** Démo : le geste est permis (« Montre-moi », Idries 2026-10-29) ; les visites de la démo restent en « Suivant ». */
  act?: boolean }
interface Env { ctx: Ctx; demo: boolean; rerender: () => void; onDone?: (t: Tour) => void; onEnd?: (t: Tour) => HTMLElement | null }

const KEY = 'arceau.guide';
const SEEK_MS = 9000;          // le temps de trouver l'élément d'une étape (la page charge ses données)
const POSE_MS = 4000;          // une page qui ne se pose pas (données très lentes) : l'étape s'affiche quand même
let env: Env | null = null;
let run: Run | null = null;
let phase: Phase = 'idle';
let token = 0;                 // invalide les attentes d'une étape passée
let current: HTMLElement | null = null;
let missing: string[] = [];
let alt = false;               // l'étape affichée est sa variante « orElse » (l'élément manquait) : la visite s'arrête après
let settled = '';              // la page (#/…) où un élément vient de manquer, chargée : l'étape suivante y cherche vite
let layer: HTMLElement | null = null;
let loop = 0;
let lastScroll = 0;
let painted: HTMLElement | null = null;   // la vue de la dernière page rendue jusqu'au bout (app.ts, par guideAfterRender)

/** La page est posée : rendue jusqu'au bout (ses données, le « ? » de l'en-tête qui décale ses boutons), et l'app ne
 *  l'a pas redessinée depuis (une nouvelle page remplace sa vue). Une page en deux temps (l'en-tête d'abord, le reste
 *  après ses données) n'est pas posée : son bouton bouge encore, et n'a pas toujours son geste. */
const posed = () => !!painted?.isConnected;
addEventListener('hashchange', () => { painted = null; });
/** Appelé après chaque page (src/guide/index.ts) : elle est posée. Une page rendue trop tard (une autre l'a remplacée)
 *  ne compte pas. */
export function pagePainted(view: HTMLElement): void { if (view.isConnected) painted = view; }

const save = () => { try { if (run) sessionStorage.setItem(KEY, JSON.stringify(run)); else sessionStorage.removeItem(KEY); } catch { /* navigation privée */ } };
const load = (): Run | null => { try { return JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Run | null; } catch { return null; } };
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const tourOf = (r: Run | null) => (r ? TOURS[r.id] ?? null : null);
/** Le geste se fait : vrai club, ou « Montre-moi » dans la démo. */
const acts = (r: Run) => !r.demo || r.act === true;
const stepsOf = (r: Run) => stepsFor(TOURS[r.id]!, !acts(r));
/** L'étape affichée : la sienne, ou sa variante quand son élément manquait (`orElse`). */
const stepNow = (): TourStep | undefined => {
  if (!run) return undefined;
  const s = stepsOf(run)[run.i];
  return s && alt && s.orElse ? { ...s, ...s.orElse, route: undefined, orElse: undefined } : s;
};
const wait = (ms: number) => new Promise(res => setTimeout(res, ms));
/** Ce que l'app a ouvert par-dessus la page (feuille, chronomètre, drill en cours…), hors du guide. */
const openDialog = (): HTMLElement | null =>
  [...document.querySelectorAll<HTMLElement>('[role="dialog"]')].filter(d => !d.closest('.gd-layer') && d.getBoundingClientRect().width > 0).pop() ?? null;

/** Rangé dans un pli fermé (`<details>` hors de son résumé) : Chrome lui garde une taille (content-visibility), mais il
 *  n'est pas à l'écran, et la page ne va pas jusqu'à lui. */
function inClosedFold(el: HTMLElement): boolean {
  for (let d = el.parentElement?.closest('details'); d; d = d.parentElement?.closest('details')) {
    if (!d.open && !d.querySelector(':scope > summary')?.contains(el)) return true;
  }
  return false;
}

/** Le premier élément visible qui porte ce data-guide ; `folded` : un élément replié compte (il est là, le guide
 *  l'ouvrira en le cherchant). */
export function findTarget(name: string, folded = false): HTMLElement | null {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-guide="${CSS.escape(name)}"]`)) {
    if (!el.isConnected || el.closest('[hidden]') || (!folded && inClosedFold(el))) continue;
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/** L'élément est rangé dans un onglet caché de la fiche de match (« Avant » pendant le direct, qui s'ouvre 30 min avant
 *  le coup d'envoi) : on ouvre cet onglet, comme la personne le ferait. */
function revealPane(name: string): void {
  const el = document.querySelector<HTMLElement>(`[data-pane][hidden] [data-guide="${CSS.escape(name)}"]`);
  const pane = el?.closest<HTMLElement>('[data-pane]')?.dataset.pane;
  if (pane) document.querySelector<HTMLButtonElement>(`[data-phase="${CSS.escape(pane)}"]`)?.click();
  revealFolded(name);
}

/** L'élément est replié (« Et aussi » de l'accueil, une rubrique fermée) ou rangé derrière « Tout voir », ou c'est une
 *  sollicitation qui n'est pas de tour (D-155) : on ouvre le pli, le bloc la montre (`guide-reveal`, src/ui/home-kit.ts). */
function revealFolded(name: string): void {
  for (const el of document.querySelectorAll<HTMLElement>(`#view [data-guide="${CSS.escape(name)}"]`)) {
    el.closest('[data-reveal]')?.dispatchEvent(new CustomEvent('guide-reveal', { detail: el }));
    for (let d = el.closest('details'); d; d = d.parentElement?.closest('details') ?? null) if (!d.open) d.open = true;
  }
}

/** Pages des étapes : ':maillots', ':maillots-match' (D-188), ':equipe' (ma 1re équipe), ':seance' (plan de la prochaine séance ; ':seance/tirage' : son tirage au sort), ':entrainement' (sa fiche), ':match' (prochain match). */
async function resolveRoute(route: string, c: Ctx, prev?: TourStep): Promise<string | null> {
  if (!route.includes(':')) return route;
  // La page où mène l'élément éclairé à l'étape d'avant (un coach de la liste…).
  if (route === ':suite') {
    const el = prev?.target ? findTarget(prev.target, true) : null;
    const a = el?.closest<HTMLAnchorElement>('a[href^="#/"]') ?? el?.querySelector<HTMLAnchorElement>('a[href^="#/"]');
    return a?.getAttribute('href') ?? null;
  }
  const team = workTeams(c).find(t => c.staffOf.has(t.id)) ?? workTeams(c)[0];
  if (route.includes(':equipe')) return team ? route.replace(':equipe', team.id) : null;
  // ':maillots' (D-188) : la file des maillots de ma première équipe où j'ai une famille ; ':maillots-match' : son match en cours.
  if (route === ':maillots' || route === ':maillots-match') {
    const s = (await import('../lib/maillots-api').then(m => m.jerseysMine()).catch(() => []))[0];
    if (!s?.active) return null;
    if (route === ':maillots') return `#/maillots/${s.team.id}`;
    return s.current ? `#/e/${s.current.event_id}/${s.current.d}` : null;
  }
  // ':tombola' (D-183) : la tombola du club en préparation ; ':tombola-finie' la dernière tirée ; ':tombola-mienne' celle de mes tickets.
  if (route.includes(':tombola')) {
    if (!c.club) return null;
    const m = await import('../lib/tombola-api');
    if (route.includes(':tombola-mienne')) { const t = (await m.tombolaMine(c.club.id).catch(() => []))[0]; return t ? route.replace(':tombola-mienne', t.id) : null; }
    const list = await m.tombolaAdmin(c.club.id).catch(() => []);
    if (route.includes(':tombola-finie')) { const t = list.find(x => x.state === 'done'); return t ? route.replace(':tombola-finie', t.id) : null; }
    const t = list.find(x => x.state === 'prep') ?? list[0];
    return t ? route.replace(':tombola', t.id) : null;
  }
  // ':tournoi' (D-177) : le tournoi du club que j'organise, en direct d'abord (celui de la démo) ; ':clos' : un tournoi clos.
  if (route.includes(':tournoi') || route.includes(':clos') || route.includes(':annonce') || route.includes(':brouillon')) {
    if (!c.club) return null;
    const list = await import('../lib/tournoi-api').then(m => m.tournamentList(c.club!.id)).catch(() => []);
    const mine = list.filter(x => x.org);
    if (route.includes(':clos')) { const t = [...mine].reverse().find(x => x.status === 'done'); return t ? route.replace(':clos', t.id) : null; }
    // D-180 : ':annonce' un tournoi annoncé (planning pas encore publié) ; ':brouillon' un tournoi pas encore annoncé.
    if (route.includes(':annonce')) { const t = mine.find(x => x.status === 'draft' && x.announced && !x.cancelled); return t ? route.replace(':annonce', t.id) : null; }
    if (route.includes(':brouillon')) { const t = mine.find(x => x.status === 'draft' && !x.announced && !x.team_id && !x.cancelled); return t ? route.replace(':brouillon', t.id) : null; }
    const t = mine.find(x => x.live) ?? mine.find(x => x.status === 'published') ?? mine.find(x => x.status !== 'draft') ?? mine[0];
    return t ? route.replace(':tournoi', t.id) : null;
  }
  const { items } = await loadAgenda(c, 21).catch(() => ({ items: [] as Awaited<ReturnType<typeof loadAgenda>>['items'] }));
  const mine = items.filter(it => c.staffOf.has(it.ev.team_id) && !it.ev.cancelled_at);
  // ':seance/tirage' : la même page, le tirage au sort ouvert (D-176).
  if (route === ':seance' || route.startsWith(':seance/')) { const it = mine.find(x => x.ev.kind === 'training'); return it ? `#/seance/${it.ev.id}/${it.occ.date}${route.slice(':seance'.length)}` : null; }
  if (route === ':entrainement') { const it = mine.find(x => x.ev.kind === 'training'); return it ? `#/e/${it.ev.id}/${it.occ.date}` : null; }
  if (route === ':match') { const it = mine.find(x => x.ev.kind === 'match'); return it ? `#/e/${it.ev.id}/${it.occ.date}` : null; }
  return null;
}

/** Le contexte de l'app, à chaque page (app.ts) : reprend une visite en cours. */
export function guideEnv(e: Env): void {
  env = e;
  if (!run) {
    const saved = load();
    if (saved && TOURS[saved.id]) { run = saved; void show(run.i, true); }
  }
}

export const tourRunning = () => run !== null;

/** Lance une visite (« Montre-moi », visite de la démo). */
export function startTour(id: string): void {
  const t = TOURS[id];
  if (!t || !env) return;
  // D-122 : jamais la visite d'un module que le club n'a pas activé (un vieux lien, un bouton resté ouvert).
  if (!shownWith(id, tourModule(id), k => modOn(env!.ctx, k), siteOn(env!.ctx), mvOpen(env!.ctx))) return;
  stopTour(false);
  missing = [];
  // Une feuille ouverte (« Sur cet écran », accueil de la démo…) se ferme : la visite prend l'écran.
  document.querySelectorAll<HTMLElement>('.sheet-backdrop[data-close]').forEach(b => b.click());
  run = { id, i: 0, demo: env.demo, act: !env.demo || !t.end };
  save();
  // Casquette de la visite (la sienne, sinon celle du rôle qu'elle montre : hat.ts) : la vue de ce rôle d'abord.
  const hat = hatFor(t, env.ctx);
  if (hat && env.ctx.hats.some(h => h.key === hat) && env.ctx.hat !== hat) {
    switchHat(env.ctx, hat, true);
    env.rerender();
  }
  void show(0, true);
}

/** Arrête la visite ; `done` : elle est allée au bout. */
export function stopTour(done = false): void {
  const t = tourOf(run);
  token++;
  run = null; phase = 'idle'; current = null; alt = false; settled = '';
  save();
  cancelAnimationFrame(loop);
  layer?.remove(); layer = null;
  document.body.classList.remove('gd-on');
  if (done && t) env?.onDone?.(t);
}

// --- Une étape ------------------------------------------------------------------------------------
async function show(i: number, navigate: boolean): Promise<void> {
  if (!run || !env) return;
  const steps = stepsOf(run);
  if (i >= steps.length) return finish();
  run.i = i; save();
  const my = ++token;
  const s = steps[i]!;
  phase = 'seek'; current = null; alt = false;
  drawSeek();
  // Une étape sans son élément (pas de séance à venir, pas encore de drill…) est sautée ; l'e2e la signale.
  const skip = (what: string) => {
    missing.push(`${run!.id}#${i + 1}:${what}`); run!.skipped = (run!.skipped ?? 0) + 1;
    settled = location.hash;
    return show(i + 1, true);
  };
  if (s.route && navigate) {
    const to = await resolveRoute(s.route, env.ctx, steps[i - 1]);
    if (my !== token) return;
    if (to && location.hash !== to && !(to === '#/' && (location.hash === '' || location.hash === '#'))) { location.hash = to; settled = ''; }
    // Pas de page (pas de séance à venir…) : l'étape est sautée. Vrai club, « :suite » introuvable (la visite reprend
    // après un rechargement) : la personne y est sans doute déjà, on y cherche l'élément.
    else if (!to && (run.demo || s.route !== ':suite')) return skip(s.route);
  }
  if (s.target) {
    // Même page qu'un élément qui vient d'y manquer, page chargée : pas de nouvelle longue attente.
    let el = await seek(s.target, my, location.hash === settled ? 800 : SEEK_MS);
    if (my !== token) return;
    // Vrai club, rien encore (pas de drill dans la bibliothèque) : la variante de l'étape, si elle en a une.
    if (!el && s.orElse && !run.demo) { el = await seek(s.orElse.target, my, 1500); if (my !== token) return; alt = !!el; }
    if (!el) return skip(s.target);
    current = el;
  }
  if (my !== token) return;
  phase = 'step'; settled = '';
  run.shown = (run.shown ?? 0) + 1; save();
  if (current) bringIntoView(current, true);
  drawStep();
}

/** Le premier élément visible qui porte ce data-guide, attendu `ms` au plus (la page charge ses données). L'élément
 *  n'est montré que sur une page posée (posed) : « + Play » est dans l'en-tête dès le premier rendu, mais son geste
 *  n'est branché et sa place définitive (le « ? » le décale) qu'une fois les plays chargés. Une page posée (rien
 *  n'y a bougé depuis 2 s, 3 s après l'arrivée) sans l'élément ne l'aura pas : on n'attend pas plus. */
async function seek(name: string, my: number, ms: number): Promise<HTMLElement | null> {
  const t0 = Date.now();
  let el: HTMLElement | null = null, moved = Date.now();
  const watch = new MutationObserver(() => { moved = Date.now(); });
  watch.observe(document.querySelector('#view') ?? document.body, { childList: true, subtree: true });
  const ready = () => posed() || Date.now() - t0 > POSE_MS;
  try {
    while (Date.now() - t0 < ms) {
      await wait(150);
      if (my !== token) return null;
      el = findTarget(name);
      if (!el) revealPane(name);
      // Une page qui se redessine (données chargées après coup) : on attend qu'elle se pose, et qu'elle le reste.
      if (el && Date.now() - t0 > 450 && ready()) {
        await wait(250);
        if (my !== token) return null;
        if (ready()) return findTarget(name) ?? el;
        continue;
      }
      if (!el && !openDialog() && posed() && Date.now() - t0 > 3000 && Date.now() - moved > 2000) return null;
    }
    return el;
  } finally { watch.disconnect(); }
}

function next(skipGesture = false): void {
  if (!run) return;
  if (alt) return finish();
  const steps = stepsOf(run);
  const s = steps[run.i]!;
  // « Suivant » sans faire le geste : les étapes qui le supposent (le chrono ouvert, un fichier choisi) sont sautées.
  let j = run.i + 1;
  if (s.tap) while (steps[j]?.real) { j++; run.skipped = (run.skipped ?? 0) + 1; }
  // Après un geste fait, la page suivante vient de la personne ; sinon le guide y mène.
  void show(j, run.demo || !s.tap || skipGesture);
}

function finish(): void {
  const t = tourOf(run);
  if (!t || !run) return;
  if (run.demo && t.end && env?.onEnd) {
    token++; phase = 'end'; current = null;
    drawEnd(t);
    return;
  }
  const { demo, shown = 0, skipped = 0 } = run;
  stopTour(shown > 0);
  // Rien n'a pu être montré : l'écran est encore vide (pas de séance, pas de drill…) ; une étape sautée : pas de « Bravo ».
  toast(!shown ? 'Rien à montrer ici pour l’instant : l’écran est encore vide.' : demo || skipped ? 'Fin de la visite.' : 'C’est fait. Bravo !');
}

/** Mode « faire » : la personne a touché l'élément éclairé ; le guide attend l'élément de l'étape suivante.
 *  * Une feuille ou un plein écran ouvert par le geste (formulaire, chronomètre) : l'étape suivante attend qu'il
 *    se ferme, sauf si son élément est dedans.
 *  * Rien ne s'est ouvert ni n'a changé de page, et l'étape suivante est sur une autre page (« Je prends », puis
 *    l'accueil) : le guide y mène.
 *  * Sinon il attend (20 minutes au plus), la page libre, l'élément du geste cerclé s'il est encore là : un
 *    assistant en plusieurs écrans (l'import), un toucher à côté du bouton. */
function afterGesture(): void {
  if (!run || phase !== 'step') return;
  const steps = stepsOf(run);
  const i = run.i;
  const my = ++token;
  phase = 'wait';
  if (i + 1 >= steps.length || alt) { setTimeout(() => { if (my === token) finish(); }, 400); return; }
  const s = stepNow()!, nextStep = steps[i + 1]!;
  drawWait(s);
  const t0 = Date.now(), hash0 = location.hash;
  let calm = Date.now();          // depuis quand rien n'est ouvert par-dessus la page
  const poll = () => {
    if (my !== token || !run) return;
    if (Date.now() - t0 > 20 * 60_000) { stopTour(); return; }
    const dialog = openDialog();
    // Replié compte : l'étape suivante le cherche et ouvre le pli.
    const el = nextStep.target ? findTarget(nextStep.target, true) : null;
    if ((el && (!dialog || dialog.contains(el))) || (!nextStep.target && !dialog)) { void show(i + 1, false); return; }
    if (dialog) calm = Date.now();
    else if (nextStep.route && location.hash === hash0 && Date.now() - calm > 1500 && Date.now() - t0 > 1500) { void show(i + 1, true); return; }
    setTimeout(poll, 250);
  };
  setTimeout(poll, 300);
}

// --- Dessin ---------------------------------------------------------------------------------------
function ensureLayer(): HTMLElement {
  if (layer?.isConnected) return layer;
  layer = document.createElement('div');
  layer.className = 'gd-layer';
  document.body.append(layer);
  document.body.classList.add('gd-on');
  layer.addEventListener('click', onLayerClick);
  return layer;
}

const dots = (i: number, n: number) => `<span class="gd-dots" aria-hidden="true">${Array.from({ length: n }, (_, k) => `<i class="${k === i ? 'on' : ''}"></i>`).join('')}</span>`;
const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);

function pillHtml(t: Tour, i: number, n: number, label?: string): string {
  return `<div class="gd-pill" role="status"><span class="ball" aria-hidden="true"></span>
    <span class="t">${esc(label ?? t.pill ?? t.kicker)} <small>· ${i + 1} / ${n}</small></span>
    <button type="button" data-gd="quit">${run?.demo ? 'Quitter' : 'Plus tard'}</button></div>`;
}

function drawSeek(): void {
  const t = tourOf(run);
  if (!t || !run) return;
  const L = ensureLayer();
  L.innerHTML = `<div class="gd-veil gd-veil-all"></div>${run.demo ? pillHtml(t, run.i, stepsOf(run).length + (t.end ? 1 : 0)) : ''}`;
  // Vrai club : une page qui tarde à venir montre la pilule (où l'on en est, « Plus tard » à portée).
  if (!run.demo) {
    const my = token, n = stepsOf(run).length, i = run.i;
    setTimeout(() => { if (my === token && phase === 'seek' && layer?.isConnected && !layer.querySelector('.gd-pill')) layer.insertAdjacentHTML('beforeend', pillHtml(t, i, n)); }, 1200);
  }
}

function drawStep(): void {
  const t = tourOf(run);
  if (!t || !run) return;
  const steps = stepsOf(run), i = run.i, s = stepNow()!;
  // La carte de fin de la démo compte pour une étape ; la variante d'une étape (orElse) est la dernière.
  const n = alt ? i + 1 : steps.length + (run.demo && t.end ? 1 : 0);
  const doIt = acts(run) && !!s.tap && !!current;
  const elText = current?.textContent ?? '';
  // Démo : le geste est permis, « Suivant » reste (il saute ce que le geste aurait ouvert).
  const left = s.aside && !run.demo ? `<button type="button" class="gd-ghost" data-gd="aside">${esc(s.aside.label)}</button>`
    : `<button type="button" class="gd-skip" data-gd="${doIt && !run.demo ? 'pass' : 'skip'}">Passer</button>`;
  const right = doIt && !run.demo ? `${dots(i, n)}<button type="button" class="gd-skip" data-gd="quit">Plus tard</button>`
    : `${dots(i, n)}<button type="button" class="gd-next" data-gd="next">${i + 1 < n && !alt ? 'Suivant →' : 'Terminer'}</button>`;
  const L = ensureLayer();
  L.innerHTML = `
    ${current ? `<div class="gd-hole${doIt ? ' gd-do' : ''}"></div>
    <div class="gd-veil" data-v="t"></div><div class="gd-veil" data-v="b"></div><div class="gd-veil" data-v="l"></div><div class="gd-veil" data-v="r"></div>
    ${doIt ? '' : '<div class="gd-veil" data-v="h"></div>'}` : '<div class="gd-veil gd-veil-all gd-dim"></div>'}
    <div class="gd-bubble ${current ? 'below' : 'center'}" role="dialog" aria-live="polite" aria-label="${esc(s.title)}">
      <div class="gd-k"><span>${esc(t.kicker)}</span><span>${i + 1} / ${n}</span></div>
      <h4>${richText(s.title, elText)}</h4>
      ${s.text ? `<p>${richText(s.text, elText)}</p>` : ''}
      ${doIt ? `<div class="gd-tap"><i aria-hidden="true">☝</i>À toi : ${richText(s.tap!, elText)}${run.demo ? ', ou « Suivant »' : ''}</div>` : ''}
      <div class="gd-act">${left}${right}</div>
    </div>
    ${run.demo ? pillHtml(t, i, n) : ''}`;
  place();
  startLoop();
  if (!doIt) L.querySelector<HTMLElement>('[data-gd="next"]')?.focus({ preventScroll: true });
}

/** Après le geste : la pilule seule, la page libre ; l'élément du geste cerclé s'il est encore là, quand rien n'est
 *  ouvert par-dessus (l'écran suivant d'un assistant, un toucher qui n'a rien ouvert). */
function drawWait(s: TourStep): void {
  const t = tourOf(run);
  if (!t || !run) return;
  cancelAnimationFrame(loop);
  const L = ensureLayer();
  const label = `À toi : ${s.tap ?? 'continue'}`.replace(/\{el\}/g, textOf(current) || 'ce bouton');
  L.innerHTML = `<div class="gd-ring" hidden></div>${pillHtml(t, run.i, stepsOf(run).length, label)}`;
  const ring = L.querySelector<HTMLElement>('.gd-ring')!;
  const t0 = Date.now();
  let el: HTMLElement | null = null, k = 0, d: HTMLElement | null = null;
  const tick = () => {
    if (phase !== 'wait' || !run || !ring.isConnected) return;
    if (!el?.isConnected || k % 12 === 0) { el = s.target ? findTarget(s.target) : null; d = openDialog(); }
    k++;
    const on = !!el && Date.now() - t0 > 700 && (!d || d.contains(el));
    ring.hidden = !on;
    if (on) {
      const r = el!.getBoundingClientRect();
      Object.assign(ring.style, { left: `${r.left - 6}px`, top: `${r.top - 6}px`, width: `${r.width + 12}px`, height: `${r.height + 12}px` });
    }
    loop = requestAnimationFrame(tick);
  };
  loop = requestAnimationFrame(tick);
}

function drawEnd(t: Tour): void {
  cancelAnimationFrame(loop);
  const L = ensureLayer();
  L.innerHTML = '<div class="gd-veil gd-veil-all gd-dim"></div>';
  const card = env?.onEnd?.(t);
  if (card) L.append(card);
}

/** Le trou, le voile autour, la bulle (dessous, dessus, ou en bas de l'écran). */
function place(): void {
  if (!layer || phase !== 'step') return;
  const bubble = layer.querySelector<HTMLElement>('.gd-bubble');
  const hole = layer.querySelector<HTMLElement>('.gd-hole');
  const vw = innerWidth, vh = innerHeight;
  const pad = 6;
  const veils = Object.fromEntries([...layer.querySelectorAll<HTMLElement>('.gd-veil[data-v]')].map(v => [v.dataset.v!, v]));
  if (!current || !current.isConnected) return;
  const r = current.getBoundingClientRect();
  const x = Math.max(0, r.left - pad), y = Math.max(0, r.top - pad);
  const w = Math.min(vw, r.right + pad) - x, h = Math.min(vh, r.bottom + pad) - y;
  if (hole) Object.assign(hole.style, { left: `${x}px`, top: `${y}px`, width: `${w}px`, height: `${h}px` });
  const set = (v: HTMLElement | undefined, a: number, b: number, c: number, d: number) => v && Object.assign(v.style, { left: `${a}px`, top: `${b}px`, width: `${Math.max(0, c)}px`, height: `${Math.max(0, d)}px` });
  set(veils.t, 0, 0, vw, y); set(veils.b, 0, y + h, vw, vh - y - h); set(veils.l, 0, y, x, h); set(veils.r, x + w, y, vw - x - w, h); set(veils.h, x, y, w, h);
  if (!bubble) return;
  const doIt = !!hole?.classList.contains('gd-do');
  const bottomBar = (document.querySelector('.tabbar')?.getBoundingClientRect().height ?? 0) + (run?.demo ? 64 : 12);
  const topBar = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;
  const fit = (): { top: number; cls: 'below' | 'above' } | null => {
    const bh = bubble.offsetHeight;
    if (y + h + 14 + bh <= vh - bottomBar) return { top: y + h + 14, cls: 'below' };
    if (y - 14 - bh >= topBar + 4) return { top: y - 14 - bh, cls: 'above' };
    return null;
  };
  bubble.classList.remove('gd-compact');
  let pos = fit();
  // Pas la place au-dessus ni au-dessous d'un élément à toucher : la bulle se fait courte, puis, s'il le faut, se pose
  // du côté opposé au bouton à toucher, qui reste visible et touchable (une grande carte, un petit écran).
  if (!pos && doIt) { bubble.classList.add('gd-compact'); pos = fit(); }
  let top: number, cls: 'below' | 'above' | 'center';
  if (pos) ({ top, cls } = pos);
  else {
    const bh = bubble.offsetHeight;
    const low = Math.max(topBar + 4, vh - bottomBar - bh), high = topBar + 4;
    const s = stepNow();
    const a = doIt ? actionOf(current, s?.tap)?.getBoundingClientRect() : null;
    top = a && (a.top + a.bottom) / 2 > (high + vh - bottomBar) / 2 ? high : low;
    cls = top > y + h / 2 ? 'below' : 'above';
  }
  bubble.classList.remove('below', 'above', 'center'); bubble.classList.add(cls);
  bubble.style.top = `${Math.round(top)}px`;
  bubble.style.setProperty('--ax', `${Math.round(Math.min(Math.max(r.left + r.width / 2 - bubble.getBoundingClientRect().left, 28), bubble.offsetWidth - 28))}px`);
}

/** Le premier ancêtre qui défile de lui-même (une feuille, le plein écran du chronomètre), sinon rien : la page. */
function scroller(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p && p !== document.body && p !== document.documentElement; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight + 2) return p;
  }
  return null;
}

/** Amène l'élément vers le haut de l'écran, sous la barre du club (ou le haut de la feuille qui le contient), en
 *  laissant la place à la bulle. Une feuille, un plein écran défilent eux-mêmes : c'est eux qu'on fait défiler. */
function bringIntoView(el: HTMLElement, force = false): void {
  const now = Date.now();
  if (!force && now - lastScroll < 900) return;
  // Une rangée qui défile de côté (la barre des casquettes, des onglets) : l'élément y vient d'abord.
  const rx = el.getBoundingClientRect();
  if (rx.left < 0 || rx.right > innerWidth) { el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' as ScrollBehavior }); lastScroll = now; }
  const r = el.getBoundingClientRect();
  const box = scroller(el);
  const top0 = box ? Math.max(0, box.getBoundingClientRect().top) : document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;
  const want = Math.max(top0 + 16, innerHeight * 0.16);
  const visible = r.top >= top0 + 8 && r.bottom <= innerHeight * 0.55;
  if (visible && !force) return;
  if (force && r.top >= top0 + 8 && r.bottom <= innerHeight * 0.5) return;
  lastScroll = now;
  if (box) {
    box.scrollTop = Math.max(0, box.scrollTop + r.top - want);
    const r2 = el.getBoundingClientRect();
    if (r2.top >= 0 && r2.bottom <= innerHeight) return;
  }
  window.scrollTo({ top: Math.max(0, scrollY + el.getBoundingClientRect().top - want), behavior: 'instant' as ScrollBehavior });
}

/** Ce qu'il faut toucher dans l'élément éclairé : lui-même s'il se touche, sinon le bouton que le geste nomme
 *  (« touche « Encourager » »), sinon le premier bouton visible, sinon le premier lien. */
export function actionOf(el: HTMLElement | null, tap?: string): HTMLElement | null {
  if (!el) return null;
  const ACT = 'button, a[href], input:not([type=hidden]), select, textarea, label, summary, [role=button], [role=tab], [role=radio]';
  if (el.matches(ACT)) return el;
  const norm = (x: string | null | undefined) => (x ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  const word = norm(/«\s*([^»]+?)\s*»/.exec(tap ?? '')?.[1]?.replace('{el}', el.textContent ?? ''));
  const all = [...el.querySelectorAll<HTMLElement>(ACT)].filter(x => { const r = x.getBoundingClientRect(); return r.width > 0 && r.height > 0; });
  // Sans nom : un bouton plutôt qu'un lien (« Mes suivis › » en tête de bloc n'est pas le geste).
  return (word && all.find(x => norm(x.textContent).includes(word) || norm(x.getAttribute('aria-label')).includes(word)))
    || all.find(x => x.tagName === 'BUTTON') || all[0] || el;
}

const textOf = (el: Element | null) => (el?.textContent ?? '').replace(/\s+/g, ' ').trim();

/** Suit l'élément (page qui se redessine, défilement, clavier) tant que l'étape est affichée. */
function startLoop(): void {
  cancelAnimationFrame(loop);
  let last = '';
  const tick = () => {
    if (phase !== 'step' || !run) return;
    const s = stepNow();
    if (s?.target && (!current || !current.isConnected)) { const el = findTarget(s.target); if (el) current = el; }
    if (current) {
      const r = current.getBoundingClientRect();
      if (r.bottom < 0 || r.top > innerHeight) bringIntoView(current);
      const k = `${Math.round(r.left)},${Math.round(r.top)},${Math.round(r.width)},${Math.round(r.height)},${innerWidth},${innerHeight}`;
      if (k !== last) { last = k; place(); }
    }
    loop = requestAnimationFrame(tick);
  };
  loop = requestAnimationFrame(tick);
}

// --- Gestes ---------------------------------------------------------------------------------------
function onLayerClick(e: MouseEvent): void {
  const b = (e.target as HTMLElement).closest<HTMLElement>('[data-gd]');
  if (!b) {
    // Un toucher à côté : le trou s'anime pour montrer où toucher.
    const hole = layer?.querySelector<HTMLElement>('.gd-hole');
    if (hole && (e.target as HTMLElement).classList.contains('gd-veil') && !reduced()) { hole.classList.remove('gd-pulse'); void hole.offsetWidth; hole.classList.add('gd-pulse'); }
    return;
  }
  const act = b.dataset.gd;
  if (act === 'next') next();
  else if (act === 'pass') next(true);
  else if (act === 'skip') { if (run?.demo) { const t = tourOf(run); if (t?.end) { run.i = stepsOf(run).length; finish(); } else stopTour(); } else next(true); }
  else if (act === 'quit') stopTour();
  else if (act === 'aside' && run) {
    const s = stepNow();
    if (s?.aside) env && asideSheet(s.aside.title, s.aside.text);
  }
}

function asideSheet(title: string, text: string): void {
  const box = document.createElement('div');
  box.className = 'gd-aside';
  box.innerHTML = `<div class="gd-aside-card" role="dialog" aria-label="${esc(title)}"><h4>${esc(title)}</h4><p>${esc(text)}</p><button type="button" class="gd-next">Compris</button></div>`;
  box.addEventListener('click', ev => { if ((ev.target as HTMLElement).closest('button') || ev.target === box) box.remove(); });
  layer?.append(box);
}

// Mode « faire » : un toucher sur l'élément éclairé (l'événement va à l'app, on note seulement).
document.addEventListener('click', (e) => {
  if (!run || !acts(run) || phase !== 'step' || !current) return;
  const s = stepNow();
  if (s?.tap && current.contains(e.target as Node)) setTimeout(afterGesture, 60);
}, true);
// Échap quitte la visite, sauf s'il ferme d'abord ce que l'app a ouvert (une feuille, le chronomètre).
document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && run && phase !== 'idle' && !openDialog()) stopTour(); });
addEventListener('resize', () => { if (phase === 'step') place(); });

// Pour scripts/e2e-tours.mjs et scripts/guide/film.mjs : l'état de la visite, et la lancer.
declare global { interface Window { __guide?: unknown } }
window.__guide = {
  /** Les visites : compte de démo qui les rejoue, casquette, étapes vues en démo. */
  list: () => Object.values(TOURS).map(t => ({ id: t.id, as: t.as, hat: t.hat ?? null, n: stepsFor(t, true).length + (t.end ? 1 : 0), end: !!t.end,
    tuto: ARTICLES.some(a => a.slug === t.id) })),
  start: (id: string) => { missing = []; startTour(id); },
  state: () => ({ id: run?.id ?? null, i: run?.i ?? -1, n: run ? stepsOf(run).length : 0, phase, demo: run?.demo ?? null,
    target: stepNow()?.target ?? null, tap: stepNow()?.tap ?? null, alt,
    found: !!current, ring: !!layer?.querySelector('.gd-ring:not([hidden])'), missing: [...missing] }),
  next: () => next(),
  stop: () => stopTour(),
};
